import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Check } from "lucide-react";
import OnboardingShell from "@/components/jk/OnboardingShell";
import { PrimaryButton, TextField } from "@/components/jk/ui";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";
import api from "@/lib/api";

const OnboardingPayout = () => {
  const navigate = useNavigate();
  const { astrologer, onboardingComplete, refresh } = useAuth();
  const { open: openMenu } = useMenu();
  const editMode = !!onboardingComplete;
  const backToMenu = () => { openMenu(); navigate(-1); };
  const [upi, setUpi] = useState("");
  const [accName, setAccName] = useState("");
  const [accNum, setAccNum] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (astrologer) {
      setUpi(astrologer.payout_upi_id || "");
      setAccName(astrologer.payout_account_name || "");
      setAccNum(astrologer.payout_account_number || "");
      setIfsc(astrologer.payout_ifsc || "");
    }
  }, [astrologer]);

  const finish = async (savePayout) => {
    setSaving(true);
    try {
      if (savePayout) {
        await api.put("/astrologer/payout", {
          payout_upi_id: upi.trim(), payout_account_name: accName.trim(),
          payout_account_number: accNum.trim(), payout_ifsc: ifsc.trim().toUpperCase(),
        });
      }
      if (editMode) {
        await refresh();
        toast.success("Payout details saved");
        setSaving(false);
        backToMenu();
        return;
      }
      await api.post("/astrologer/complete-onboarding");
      await refresh();
      navigate("/onboarding/complete", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Try again.");
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      step={editMode ? null : 4}
      title={editMode ? "Edit Payout" : "Get Paid"}
      subtitle="Add where you'd like to receive your consultation earnings."
      onBack={() => editMode ? backToMenu() : navigate("/onboarding/availability")}
      testid="onboarding-payout-screen"
      footer={
        <>
          <PrimaryButton testid="payout-continue" onClick={() => finish(true)} disabled={saving} icon={editMode ? <Check size={18} strokeWidth={2} /> : <ArrowRight size={18} strokeWidth={2} />}>
            {saving ? "Saving…" : editMode ? "Save changes" : "Complete Setup"}
          </PrimaryButton>
          {!editMode && (
            <button data-testid="payout-skip" onClick={() => finish(false)} disabled={saving} className="mt-4 w-full text-center text-[13px] font-medium text-[#666666]">
              Skip for now
            </button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        <TextField testid="upi-input" label="UPI ID" placeholder="yourname@upi" value={upi} onChange={(e) => setUpi(e.target.value)} />

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-[#e9e6e1]" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#aaaaaa]">or bank account</span>
          <div className="h-px flex-1 bg-[#e9e6e1]" />
        </div>

        <TextField testid="acc-name-input" label="Account Holder Name" placeholder="As per bank records" value={accName} onChange={(e) => setAccName(e.target.value)} />
        <TextField testid="acc-num-input" label="Account Number" inputMode="numeric" placeholder="Enter account number" value={accNum} onChange={(e) => setAccNum(e.target.value)} />
        <TextField testid="ifsc-input" label="IFSC Code" placeholder="e.g. HDFC0001234" value={ifsc} onChange={(e) => setIfsc(e.target.value)} />
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-[14px] bg-[var(--jk-periwinkle)] p-4" data-testid="razorpay-trust">
        <ShieldCheck size={20} strokeWidth={1.75} className="mt-0.5 shrink-0 text-[#111111]" />
        <div>
          <p className="text-[13px] font-semibold text-[#111111]">Secured by Razorpay</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-[#555]">
            Your details are encrypted and processed securely. Payments are settled directly to your account.
          </p>
        </div>
      </div>
    </OnboardingShell>
  );
};

export default OnboardingPayout;
